import { Link } from 'react-router-dom';
import { ArrowRight, HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQ = () => {
  const faqs = [
    {
      question: 'What areas do you serve?',
      answer: 'We currently serve Chitradurga and surrounding areas including Basaweshwara Nagar. We bring our premium doorstep car wash services directly to your home, office, or apartment complex within our service area.',
    },
    {
      question: 'How do I book a car wash?',
      answer: 'You can book through multiple channels: Call us at 9044499957, message us on WhatsApp, or use our online booking form. WhatsApp is the quickest way to get a response and book your service.',
    },
    {
      question: 'What are your service hours?',
      answer: 'We operate from 7:00 AM to 7:00 PM, all days of the week. You can book a time slot that works best for your schedule.',
    },
    {
      question: 'How long does a car wash take?',
      answer: 'A regular wash typically takes 30-45 minutes depending on the car size and condition. Quick exterior rinse takes about 15-20 minutes.',
    },
    {
      question: 'What products do you use?',
      answer: 'We use only premium, eco-friendly car care products that are safe for your vehicle\'s paint and interior. Our products are water-efficient and environmentally responsible.',
    },
    {
      question: 'Do I need to provide water or electricity?',
      answer: 'No, our team comes fully equipped with all necessary supplies, tools, and water. You don\'t need to arrange anything – just park your car and leave the rest to us.',
    },
    {
      question: 'What is the VIP Membership?',
      answer: 'VIP Membership is our premium service tier that includes: No booking required (just call), 15-minute response guarantee, on-demand doorstep service, top priority, zero waiting time, and a dedicated VIP service team.',
    },
    {
      question: 'How does the Monthly Subscription work?',
      answer: 'Our Monthly Wash Subscription lets you pay for 4 washes and get 1 FREE. It\'s valid for 30 days from the subscription date, non-transferable, and non-refundable. Great value for regular car care!',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept cash, UPI payments (GPay, PhonePe, Paytm), and bank transfers. Payment is collected after the service is completed to your satisfaction.',
    },
    {
      question: 'What if I\'m not satisfied with the service?',
      answer: 'Your satisfaction is our priority. If you\'re not happy with any aspect of our service, please let us know immediately and we\'ll address it right away. We aim for 100% customer satisfaction.',
    },
    {
      question: 'Can I get a discount for multiple cars?',
      answer: 'Yes! We offer several multi-car discounts: Twin Cars (same location) get 10% off each, and our Neighbor\'s Cars Wash Offer provides 10-20% off depending on the number of cars washed together.',
    },
    {
      question: 'Do you offer services for fleet operators?',
      answer: 'Yes, we offer fleet maintenance packages for businesses. Contact us for corporate pricing.',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="faq-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            <HelpCircle className="h-4 w-4 mr-2" />
            Help Center
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
            Find answers to common questions about our services, booking, and pricing.
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="border border-slate-200 rounded-2xl overflow-hidden"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-slate-50 transition-colors text-left">
                  <span className="font-heading font-semibold text-[#0B2C5D] pr-4">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="text-[#64748B] leading-relaxed">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
            Still Have Questions?
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mb-8">
            Can't find what you're looking for? Our team is happy to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/919044499957?text=Hi%20Washora,%20I%20have%20a%20question."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-700 transition-all flex items-center justify-center"
              data-testid="faq-whatsapp"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Ask on WhatsApp
            </a>
            <Link
              to="/contact"
              className="bg-white text-[#0B2C5D] border-2 border-[#0B2C5D] px-8 py-4 rounded-full font-semibold hover:bg-[#0B2C5D] hover:text-white transition-all flex items-center justify-center"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Book your premium doorstep car wash today.
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg"
            data-testid="faq-book-now"
          >
            Book Now <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
